import { Box, Container, Heading, Text, VStack, useColorMode } from '@chakra-ui/react'

export function PrivacyPage() {
  const { colorMode } = useColorMode()

  return (
    <Container maxW="container.md" py={16}>
      <VStack spacing={8} align="stretch">
        <VStack spacing={4} align="start">
          <Heading as="h1" size="2xl">
            Privacy Policy
          </Heading>
          <Text color="stone.500">
            Last updated: January 31, 2026
          </Text>
        </VStack>

        <Box
          p={8}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
          bg={colorMode === 'dark' ? 'stone.800' : 'white'}
        >
          <VStack spacing={6} align="stretch">
            <Section
              title="1. Introduction"
              content="This Privacy Policy explains how myrkup.com ('we', 'our', or 'us') collects, uses, and protects your information when you use our Service. We are committed to protecting your privacy and ensuring the security of your data."
            />

            <Section
              title="2. Information We Collect"
              content="We collect minimal information necessary to provide the Service: (a) Account information: API keys are generated anonymously with no email or password required. Email addresses are collected only if you submit a waitlist or enterprise contact form; (b) Usage data: API request logs, share metadata (creation dates, expiration dates, file sizes), and analytics data; (c) Technical data: IP addresses, browser type, and device information for security and performance monitoring."
            />

            <Section
              title="3. Encryption"
              content="Every share on myrkup.com is encrypted with AES-256-GCM before storage. The server only stores ciphertext. When content is submitted through the browser, encryption happens client-side using Argon2id key derivation. When content comes through MCP or the REST API, encryption happens server-side using PBKDF2 key derivation before storage. In all cases, only someone with the link and passcode can decrypt the content."
            />

            <Section
              title="4. How We Use Your Information"
              content="We use collected information to: (a) Provide and maintain the Service; (b) Authenticate API requests and manage access controls; (c) Monitor usage limits and enforce rate limiting; (d) Detect and prevent abuse, fraud, and security incidents; (e) Analyze usage patterns to improve the Service; and (f) Communicate with you about service updates and important notices."
            />

            <Section
              title="5. Data Storage and Security"
              content="Your encrypted content is stored on Cloudflare R2 storage. Metadata is stored in Cloudflare D1 databases. We implement industry-standard security measures including encryption in transit (TLS), access controls, and regular security audits. However, no method of transmission over the internet is 100% secure."
            />

            <Section
              title="6. Data Retention"
              content="Encrypted content is retained according to the expiration date set when creating a share. Expired and revoked shares are automatically deleted by an hourly cleanup process, which removes both the encrypted content and all associated metadata. You may delete your shares at any time through the management interface."
            />

            <Section
              title="7. Third-Party Services"
              content="We use the following third-party services: (a) Cloudflare: Infrastructure provider for hosting, storage, and CDN services; (b) Cloudflare Turnstile: CAPTCHA service for bot protection. These services have their own privacy policies governing their use of data."
            />

            <Section
              title="8. Cookies and Tracking"
              content="We use essential cookies for authentication and session management. We do not use third-party advertising cookies or cross-site tracking. You can configure your browser to refuse cookies, but this may limit functionality of the Service."
            />

            <Section
              title="9. Your Rights"
              content="You have the right to: (a) Access your account information and share metadata; (b) Delete your shares and associated data at any time; (c) Export your encrypted content; (d) Request deletion of your account and associated data; and (e) Opt out of non-essential communications."
            />

            <Section
              title="10. Children's Privacy"
              content="The Service is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly."
            />

            <Section
              title="11. International Data Transfers"
              content="Your data may be transferred to and processed in countries other than your country of residence. We rely on Cloudflare's global infrastructure, which complies with applicable data protection regulations including GDPR."
            />

            <Section
              title="12. Data Breach Notification"
              content="In the event of a data breach that may affect your encrypted content or account information, we will notify affected users within 72 hours of becoming aware of the breach, in accordance with applicable data protection laws."
            />

            <Section
              title="13. Changes to This Privacy Policy"
              content="We may update this Privacy Policy from time to time. We will notify users of material changes by updating the 'Last updated' date and, for significant changes, by email or prominent notice on the Service."
            />

            <Section
              title="14. Contact Us"
              content="If you have questions about this Privacy Policy or our data practices, please contact us through our support channels. For data subject access requests or privacy concerns, please email us with 'Privacy Request' in the subject line."
            />

            <Section
              title="15. Zero-Knowledge Architecture"
              content="Our encryption architecture means the server only stores ciphertext. We cannot access, read, or decrypt your content. While this provides maximum privacy and security, it also means that if you lose your passcode, we cannot recover your content. Please store your passcodes securely."
            />
          </VStack>
        </Box>
      </VStack>
    </Container>
  )
}

function Section({ title, content }: { title: string; content: string }) {
  return (
    <VStack spacing={2} align="stretch">
      <Heading as="h2" size="md">
        {title}
      </Heading>
      <Text color="stone.600" _dark={{ color: 'stone.300' }} lineHeight="tall">
        {content}
      </Text>
    </VStack>
  )
}
